import {
  PASSWORD_CHECKLIST_REQUIRES_INPUT,
  PASSWORD_MAX_LENGTH,
  PASSWORD_REQUIRED_MESSAGE,
  PASSWORD_RULE_SPECS,
  PASSWORD_SECURE_MIN_LENGTH,
  PASSWORD_WEAK_LIST,
  type PasswordRuleSpec,
} from "../generated/passwordPolicy";

const regexCache = new Map<string, RegExp>();

function getRegex(pattern: string): RegExp {
  let compiled = regexCache.get(pattern);
  if (!compiled) {
    compiled = new RegExp(pattern);
    regexCache.set(pattern, compiled);
  }
  return compiled;
}

const WEAK_PASSWORDS_LOWER = new Set(
  PASSWORD_WEAK_LIST.map((value) => value.toLowerCase())
);

export function formatPasswordPolicyMessage(
  template: string,
  minLength: number,
  maxLength: number = PASSWORD_MAX_LENGTH
): string {
  return template
    .replace("{minLength}", String(minLength))
    .replace("{maxLength}", String(maxLength));
}

export function passwordRulePasses(
  rule: PasswordRuleSpec,
  password: string,
  minLength: number
): boolean {
  switch (rule.type) {
    case "min_length":
      return password.length >= minLength;
    case "max_length":
      return password.length <= PASSWORD_MAX_LENGTH;
    case "regex":
      return rule.pattern ? getRegex(rule.pattern).test(password) : true;
    case "regex_forbid":
      return rule.pattern ? !getRegex(rule.pattern).test(password) : true;
    case "blocklist_exact":
      return !WEAK_PASSWORDS_LOWER.has(password.toLowerCase());
    default:
      return true;
  }
}

/** Collect validation errors for a password using the secure registration profile. */
export function collectPasswordPolicyErrors(
  password: string,
  minLength: number = PASSWORD_SECURE_MIN_LENGTH
): string[] {
  if (typeof password !== "string" || !password) {
    return [PASSWORD_REQUIRED_MESSAGE];
  }
  return PASSWORD_RULE_SPECS.filter(
    (rule) => !passwordRulePasses(rule, password, minLength)
  ).map((rule) =>
    formatPasswordPolicyMessage(rule.errorMessage, minLength, PASSWORD_MAX_LENGTH)
  );
}

export interface PasswordRequirementCheck {
  id: string;
  label: string;
  met: boolean;
}

export function getPasswordRequirementChecksFromPolicy(
  password: string,
  minLength: number = PASSWORD_SECURE_MIN_LENGTH
): PasswordRequirementCheck[] {
  const hasInput = password.length > 0;
  return PASSWORD_RULE_SPECS.map((rule) => {
    const passes = passwordRulePasses(rule, password, minLength);
    const requiresInput = PASSWORD_CHECKLIST_REQUIRES_INPUT.has(rule.id);
    return {
      id: rule.id,
      label: formatPasswordPolicyMessage(rule.label, minLength, PASSWORD_MAX_LENGTH),
      met: requiresInput ? hasInput && passes : passes,
    };
  });
}

/** Scoreable rules for strength meters (character-class requirements only). */
export const PASSWORD_STRENGTH_RULE_IDS = [
  "length",
  "uppercase",
  "lowercase",
  "digit",
  "special",
] as const;

export function getPasswordStrengthFromPolicy(
  password: string,
  minLength: number = PASSWORD_SECURE_MIN_LENGTH
): { score: number; label: string; color: string; feedback: string[] } {
  const checks = getPasswordRequirementChecksFromPolicy(password, minLength);
  const scoreable = checks.filter((check) =>
    (PASSWORD_STRENGTH_RULE_IDS as readonly string[]).includes(check.id)
  );
  const score = scoreable.filter((check) => check.met).length;
  const feedback = scoreable.filter((check) => !check.met).map((check) => check.label);

  let label: string;
  let color: string;

  switch (score) {
    case 0:
    case 1:
      label = "Very Weak";
      color = "text-red-600";
      break;
    case 2:
      label = "Weak";
      color = "text-orange-600";
      break;
    case 3:
      label = "Fair";
      color = "text-yellow-600";
      break;
    case 4:
      label = "Good";
      color = "text-blue-600";
      break;
    case 5:
      label = "Strong";
      color = "text-green-600";
      break;
    default:
      label = "Very Weak";
      color = "text-red-600";
  }

  return { score, label, color, feedback };
}
